import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('hariwood_user');
      const token = localStorage.getItem('hariwood_token');
      
      if (storedUser && token) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser); // Set initial data from storage first for speed
          
          // Then fetch fresh data from server to get full avatar etc.
          const freshUser = await api.getUser(parsedUser.id);
          
          try {
            const artist = await api.getArtistByUserId(parsedUser.id);
            if (artist && artist.image && artist.image !== 'https://placehold.co/150' && artist.image !== 'HAS_IMAGE') {
              freshUser.avatar = artist.image;
            }
          } catch (err) {
            console.error('Failed to load artist avatar:', err);
          }
          
          setUser(freshUser);
          // Sync storage with fresh data (keeping truncation if needed)
          const storageUser = { ...freshUser };
          if (storageUser.avatar && storageUser.avatar.length > 100000) {
            storageUser.avatar = 'HAS_IMAGE';
          }
          localStorage.setItem('hariwood_user', JSON.stringify(storageUser));
        } catch (error) {
          console.error('Failed to refresh user session:', error);
          // If token was invalid or user deleted, might want to logout
          // logout();
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const data = await api.login(email, password);
      
      try {
        const artist = await api.getArtistByUserId(data.user.id);
        if (artist && artist.image && artist.image !== 'https://placehold.co/150' && artist.image !== 'HAS_IMAGE') {
          data.user.avatar = artist.image;
        }
      } catch (err) {
        console.error('Failed to load artist avatar during login:', err);
      }
      
      setUser(data.user);
      localStorage.setItem('hariwood_user', JSON.stringify(data.user));
      localStorage.setItem('hariwood_token', data.token);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const data = await api.register(userData);
      setUser(data.user);
      localStorage.setItem('hariwood_user', JSON.stringify(data.user));
      localStorage.setItem('hariwood_token', data.token);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('hariwood_user');
    localStorage.removeItem('hariwood_token');
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    const storageUser = { ...updatedUser };
    if (storageUser.avatar && storageUser.avatar.length > 100000) {
      storageUser.avatar = 'HAS_IMAGE'; 
    }
    localStorage.setItem('hariwood_user', JSON.stringify(storageUser));
  };

  const googleLogin = async (credential) => {
    try {
      const data = await api.googleLogin(credential);
      setUser(data.user);
      localStorage.setItem('hariwood_user', JSON.stringify(data.user));
      localStorage.setItem('hariwood_token', data.token);
      return data;
    } catch (error) {
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, updateUser, googleLogin, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
